import React from 'react';
import { Button, Card, CardContent, Grid, Slider, Stack, Typography } from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { FDataGrid } from '../grid';
import { ISensitivityControl, ISensitivityResultRow } from './simulation.models';

type Props = {
  controls: ISensitivityControl[];
  rows: ISensitivityResultRow[];
  rowCount: number;
  paginationModel: GridPaginationModel;
  loading?: boolean;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onRun: (records: ISensitivityControl[]) => void;
  onRefresh: () => void | Promise<void>;
};
type State = { controls: ISensitivityControl[] };

class SimulationSensitivityPanel extends React.Component<Props, State> {
  state: State = { controls: this.props.controls || [] };
  componentDidUpdate(prev: Props) { if (prev.controls !== this.props.controls) this.setState({ controls: this.props.controls || [] }); }

  columns: GridColDef[] = [
    { field: 'drivercode', headerName: 'Variable', width: 145 },
    { field: 'drivername', headerName: 'Name', minWidth: 200, flex: 1 },
    { field: 'changepercent', headerName: 'Change %', width: 115, type: 'number' },
    { field: 'basevalue', headerName: 'Base', width: 130, type: 'number' },
    { field: 'stressedvalue', headerName: 'Stressed', width: 130, type: 'number' },
    { field: 'revenueimpact', headerName: 'Revenue Impact', width: 145, type: 'number' },
    { field: 'costimpact', headerName: 'Cost Impact', width: 135, type: 'number' },
    { field: 'partialresultimpact', headerName: 'Partial Result Impact', width: 175, type: 'number' },
    { field: 'capacityimpact', headerName: 'Capacity Impact', width: 155, type: 'number' },
    { field: 'severity', headerName: 'Severity', width: 115 },
    { field: 'interpretation', headerName: 'Interpretation', minWidth: 260, flex: 1 },
  ];

  setControl = (drivercode: string, changepercent: number) => this.setState({ controls: this.state.controls.map((x) => x.drivercode === drivercode ? { ...x, changepercent } : x) });
  getRowId = (r: ISensitivityResultRow) => r.id || r.rid || r.rix || `${r.runix}-${r.drivercode}-${r.changepercent}`;

  renderControl(c: ISensitivityControl) {
    return <Grid size={{ xs: 12, md: 4 }} key={c.drivercode}><Card elevation={1}><CardContent><Stack spacing={1}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{c.drivername}</Typography>
      <Typography variant="h6">{c.changepercent}%</Typography>
      <Slider value={c.changepercent} min={c.minpercent} max={c.maxpercent} step={c.steppercent} onChange={(_, v) => this.setControl(c.drivercode, Number(v))} />
    </Stack></CardContent></Card></Grid>;
  }

  render() {
    return <Stack spacing={2}>
      <Grid container spacing={2}>{this.state.controls.map((c) => this.renderControl(c))}</Grid>
      <Button variant="contained" startIcon={<ScienceIcon />} onClick={() => this.props.onRun(this.state.controls)} disabled={this.props.loading || !this.state.controls.length}>Run Sensitivity Set</Button>
      <FDataGrid title="Sensitivity Comparison Set" subtitle="Persisted what-if results for external and operational drivers." rows={this.props.rows || []} columns={this.columns} loading={this.props.loading} height={500} showQuickFilter refresh={this.props.onRefresh}
        paginationMode="server" rowCount={this.props.rowCount} paginationModel={this.props.paginationModel} onPaginationModelChange={this.props.onPaginationModelChange} pageSizeOptions={[25, 50, 100]} getRowId={this.getRowId} />
    </Stack>;
  }
}
export default SimulationSensitivityPanel;
